import React, {useCallback, useMemo, useState} from 'react';
import type {GetDepartmentListRequest} from 'src/api/ProactApiClient';
import type {
  AutocompleteProps} from 'src/design/atoms/Autocomplete';
import {
  Autocomplete
} from 'src/design/atoms/Autocomplete';
import {useGetDepartmentSelectOptionsQuery} from 'src/redux/queries/department';
import useDebounce from 'src/shared/hooks/useDebounce';
import type {ISelectOption} from 'src/types/Form';

export interface DepartmentSelectBoxProps
  extends Omit<AutocompleteProps, 'options' | 'value'> {
  queryOptions?: Partial<GetDepartmentListRequest>;
  value?: string;
}
export const DepartmentSelectBox: React.FC<DepartmentSelectBoxProps> =
  React.memo(({queryOptions, value, ...other}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const debouncedSearchTerm = useDebounce(searchTerm, 400);

    const {data, isFetching, isLoading} = useGetDepartmentSelectOptionsQuery({
      ...queryOptions,
      start: 0,
      length: 50,
      search: debouncedSearchTerm || queryOptions?.search,
      sortColumn: queryOptions?.sortColumn ?? 'code',
      sortDirection: queryOptions?.sortDirection ?? 'asc',
    });

    const options = useMemo(
      () => (data?.data ?? []) as ISelectOption[],
      [data?.data]
    );
    const selectedOption = useMemo(
      () => options?.find(i => String(i.id) === String(value)) ?? null,
      [options, value]
    );

    const handleInputChange = useCallback(
      (event: React.SyntheticEvent, newInputValue: string) => {
        setSearchTerm(newInputValue);
      },
      []
    );

    const isSearching = isFetching && debouncedSearchTerm !== '';

    return (
      <Autocomplete
        {...other}
        disabled={isLoading || other.disabled}
        options={options}
        value={selectedOption}
        onInputChange={handleInputChange}
        loading={isSearching}
      />
    );
  });
